// ============================================
// ボス関連UI表示
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import { getBossWarning, getEnemies, getBossName } from '../../store/index';
import { getGameAreaX, getGameAreaCenterX, getRightPanelCenterX } from '../../utils/helpers';
import { HUD_COLORS } from '../../config/hud-colors';
import type { BossEnemy } from '../../types/game-types';

// 右パネル ボスHP表示 Y座標
const BOSS_NAME_Y = 350;
const BOSS_BAR_Y = 370;
const BOSS_BAR_WIDTH = 100;
const BOSS_BAR_HEIGHT = 8;

// ボスHP フィルアニメーション
const BOSS_FILL_DURATION = 60;  // ゲージが満ちるまでのフレーム数
let _bossActive = false;
let _bossFillTimer = 0;

/**
 * ボス警告表示（ゲームエリア内）
 */
export function drawBossWarning(): void {
    const p = getP5();
    const bossWarning = getBossWarning();

    if (!bossWarning) {
        return;
    }

    const warningPulse = p.sin(p.frameCount * 0.3) * 0.5 + 0.5;
    const gameAreaX = getGameAreaX();
    const centerX = getGameAreaCenterX();

    // 背景を赤く点滅（ゲームエリア内のみ）
    p.fill(HUD_COLORS.red[0], HUD_COLORS.red[1], HUD_COLORS.red[2], 30 * warningPulse);
    p.rect(gameAreaX, 0, CONFIG.width, CONFIG.height);

    // WARNING表示
    p.textAlign(p.CENTER, p.CENTER);
    p.textFont('Orbitron');
    p.fill(HUD_COLORS.red[0], HUD_COLORS.red[1], HUD_COLORS.red[2], 200 + 55 * warningPulse);
    p.textSize(40);
    p.text('WARNING', centerX, CONFIG.height / 3);

    p.fill(255, 255, 0, 200 + 55 * warningPulse);
    p.textSize(24);
    p.text('BOSS INCOMING!', centerX, CONFIG.height / 3 + 45);
}

/**
 * ボスHPバー表示（右パネル、常時表示）
 * ボス不在時は空インジケーター、出現時にゲージが増える演出
 */
export function drawBossHPBar(): void {
    const p = getP5();
    const boss = getEnemies().find(e => e.type === 'boss') as BossEnemy | undefined;
    const rightCenterX = getRightPanelCenterX();
    const barX = rightCenterX - BOSS_BAR_WIDTH / 2;
    const ctx = p.drawingContext as CanvasRenderingContext2D;

    // ボス出現/消滅検出
    if (boss && !_bossActive) {
        _bossActive = true;
        _bossFillTimer = BOSS_FILL_DURATION;
    } else if (!boss && _bossActive) {
        _bossActive = false;
        _bossFillTimer = 0;
    }

    // フィルアニメーションのカウントダウン
    if (_bossFillTimer > 0) {
        _bossFillTimer--;
    }

    // ラベル（Share Tech Mono）
    const labelAlpha = boss ? 255 : 77;
    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.textSize(9);

    if (boss) {
        // ボス名（点滅 ⚠ マーク付き）
        const bossName = getBossName() || 'UNKNOWN';
        const blinkAlpha = p.frameCount % 40 < 20 ? 255 : 128;
        p.fill(HUD_COLORS.red[0], HUD_COLORS.red[1], HUD_COLORS.red[2], blinkAlpha);
        p.text(`\u26A0 ${bossName}`, rightCenterX, BOSS_NAME_Y);
    } else {
        // 非アクティブ時: BOSS HPラベル
        p.fill(HUD_COLORS.red[0], HUD_COLORS.red[1], HUD_COLORS.red[2], labelAlpha);
        p.text('BOSS HP', rightCenterX, BOSS_NAME_Y);
    }

    // HPバー背景（常時表示）
    ctx.save();
    if (boss) {
        ctx.fillStyle = `rgba(${HUD_COLORS.red[0]}, ${HUD_COLORS.red[1]}, ${HUD_COLORS.red[2]}, 0.15)`;
        ctx.strokeStyle = `rgba(${HUD_COLORS.red[0]}, ${HUD_COLORS.red[1]}, ${HUD_COLORS.red[2]}, 0.3)`;
    } else {
        // 空の場合は黒塗り
        ctx.fillStyle = 'rgba(15, 15, 20, 0.9)';
        ctx.strokeStyle = 'rgba(60, 60, 70, 0.4)';
    }
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(barX, BOSS_BAR_Y, BOSS_BAR_WIDTH, BOSS_BAR_HEIGHT, 2);
    ctx.fill();
    ctx.stroke();

    // HPバー本体（ボス存在時のみ）
    if (boss) {
        const hpRatio = boss.hp / boss.maxHp;
        // フィルアニメーション: 出現直後は0→hpRatioへ補間
        const fillProgress = _bossFillTimer > 0
            ? 1 - _bossFillTimer / BOSS_FILL_DURATION
            : 1;
        const displayRatio = hpRatio * fillProgress;

        if (displayRatio > 0) {
            const fillWidth = BOSS_BAR_WIDTH * displayRatio;
            const gradient = ctx.createLinearGradient(barX, BOSS_BAR_Y, barX + fillWidth, BOSS_BAR_Y);
            gradient.addColorStop(0, `rgb(${HUD_COLORS.red[0]}, ${HUD_COLORS.red[1]}, ${HUD_COLORS.red[2]})`);
            gradient.addColorStop(1, 'rgb(255, 102, 136)');

            ctx.shadowColor = `rgb(${HUD_COLORS.red[0]}, ${HUD_COLORS.red[1]}, ${HUD_COLORS.red[2]})`;
            ctx.shadowBlur = 10;
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.roundRect(barX, BOSS_BAR_Y, fillWidth, BOSS_BAR_HEIGHT, 2);
            ctx.fill();
            ctx.shadowBlur = 0;
        }

        // パーセント表示
        p.noStroke();
        p.textFont('Share Tech Mono');
        p.textSize(9);
        p.fill(255, 255, 255, 128);
        p.textAlign(p.CENTER, p.TOP);
        p.text(`${Math.round(displayRatio * 100)}%`, rightCenterX, BOSS_BAR_Y + BOSS_BAR_HEIGHT + 4);
    } else {
        // 非アクティブ時: ---表示
        p.noStroke();
        p.textFont('Share Tech Mono');
        p.textSize(9);
        p.fill(80);
        p.textAlign(p.CENTER, p.TOP);
        p.text('---', rightCenterX, BOSS_BAR_Y + BOSS_BAR_HEIGHT + 4);
    }

    ctx.restore();
}
